import { CustomTypeEnum, type Lever } from '@semanticedge/persistence-service-noheaders-client/src'
import { INTEGER_PATTERN, LIST_VALUE_TYPES } from '../constants/lever.js'
import type { LeverFormValue, LeverPrimitiveValue, LeverValueType } from '../types/lever.js'

type LeverValueParser = (value: string) => LeverPrimitiveValue | null

const parseBoolean: LeverValueParser = (value) => {
	if (value !== 'true' && value !== 'false') return null

	return value === 'true'
}

const parseInteger: LeverValueParser = (value) =>
	INTEGER_PATTERN.test(value) ? Number(value) : null

const parseFiniteNumber: LeverValueParser = (value) => {
	const parsedValue = Number(value)

	return Number.isFinite(parsedValue) ? parsedValue : null
}

const LEVER_VALUE_PARSERS: Partial<Record<CustomTypeEnum, LeverValueParser>> = {
	[CustomTypeEnum.Boolean]: parseBoolean,
	[CustomTypeEnum.Integer]: parseInteger,
	[CustomTypeEnum.Float]: parseFiniteNumber,
	[CustomTypeEnum.BooleanList]: parseBoolean,
	[CustomTypeEnum.IntegerList]: parseInteger,
	[CustomTypeEnum.FloatList]: parseFiniteNumber
}

/**
 * Converts a value to a string. Done because the generated client type is object, but we want to use a string.
 * @param value - The value to convert.
 * @returns The string representation of the value.
 */
const toFieldText = (value: unknown): string => {
	if (value == null) return ''
	if (typeof value === 'string') return value
	if (typeof value === 'number' || typeof value === 'boolean') return String(value)

	try {
		return JSON.stringify(value)
	} catch {
		return String(value)
	}
}

export const isListValueType = (valueType?: LeverValueType): boolean =>
	!!valueType && LIST_VALUE_TYPES.has(valueType)

export const toLeverValue = (
	value: string,
	leverType?: CustomTypeEnum
): LeverPrimitiveValue | null => {
	if (!value.trim()) return null

	const parse = leverType ? LEVER_VALUE_PARSERS[leverType] : undefined

	return parse ? parse(value) : value
}

export const applyBooleanDefault = (
	leverType: CustomTypeEnum | undefined,
	formValue: LeverFormValue
): LeverFormValue =>
	leverType === CustomTypeEnum.Boolean && !formValue.value
		? { ...formValue, value: 'false' }
		: formValue

export const toLeverFormValue = (defaultValue: unknown): LeverFormValue =>
	Array.isArray(defaultValue)
		? { value: '', values: defaultValue.map(toFieldText) }
		: { value: toFieldText(defaultValue), values: [] }

export const toLeverDefaultValue = (
	leverType: CustomTypeEnum | undefined,
	{ value, values }: LeverFormValue
): Lever['defaultValue'] => {
	const typedValue = isListValueType(leverType)
		? values
				.map((item) => toLeverValue(item, leverType))
				.filter((item): item is LeverPrimitiveValue => item !== null)
		: toLeverValue(value, leverType)

	return typedValue as unknown as Lever['defaultValue']
}

export const toLeverValidationInput = (
	leverType: CustomTypeEnum | undefined,
	{ value, values }: LeverFormValue
): string => (isListValueType(leverType) ? values.join(',') : value)
